import { ReloadOutlined } from '@ant-design/icons';
import { type IWarehouseDetail } from '@mahawi/eshop-common/src/types';
import { type UnknownAction } from '@reduxjs/toolkit';
import { Button, Input, Select, Space } from 'antd';
import { type DefaultOptionType } from 'antd/lib/select';
import React, { type ChangeEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type RootState } from '../../reducers';
import { SPACING } from '../../theme/tokens';

type WarehouseProductFilterCallback = (
  warehouseUuid: string,
  minimalWeight: number,
  maximalWeight: number,
  minimalPrice: number,
  maximalPrice: number,
  minimalDescriptionLength: number,
) => UnknownAction;

function WarehouseProductFilter({
  cb,
}: {
  cb: WarehouseProductFilterCallback;
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const warehouseWarehouses = useSelector(
    (s: RootState) => s.Warehouse.warehouses,
  );
  const warehouseUpdatedAt = useSelector(
    (s: RootState) => s.Warehouse.updatedAt,
  );

  const [warehouseOptions, setWarehouseOptions] = useState<DefaultOptionType[]>(
    [],
  );
  const [warehouseUuid, setWarehouseUuid] = useState<string | null>(null);

  const [minimalWeight, setMinimalWeight] = useState<number>(0.001);
  const [maximalWeight, setMaximalWeight] = useState<number>(30);
  const [minimalPrice, setMinimalPrice] = useState<number>(0);
  const [maximalPrice, setMaximalPrice] = useState<number>(60000);
  const [minimalDescriptionLength, setMinimalDescriptionLength] =
    useState<number>(100);

  useEffect((): void => {
    const warehouseOptionsUE: DefaultOptionType[] =
      warehouseWarehouses
        ?.filter((warehouse: IWarehouseDetail): boolean => warehouse.isActive)
        .map((warehouse: IWarehouseDetail): DefaultOptionType => ({
          label: warehouse.name,
          value: warehouse.uuid,
        })) || [];

    setWarehouseOptions(warehouseOptionsUE);
  }, [warehouseWarehouses, warehouseUpdatedAt]);

  return (
    <Space size={SPACING.lg}>
      <Space size={SPACING.lg} orientation="vertical">
        <Select
          style={{ width: 200 }}
          placeholder="Select warehouse"
          showSearch={{ optionFilterProp: 'label' }}
          options={warehouseOptions}
          onChange={(value: string): void => {
            setWarehouseUuid(value);
          }}
        />
      </Space>

      <Space size={SPACING.lg} orientation="vertical">
        <Space.Compact>
          <Button type="default" style={{ pointerEvents: 'none' }}>
            Minimal weight
          </Button>
          <Input
            type="number"
            step="0.001"
            value={minimalWeight}
            onChange={(event: ChangeEvent<HTMLInputElement>): void => {
              setMinimalWeight(Number(event.target.value));
            }}
          />
          <Button type="default" style={{ pointerEvents: 'none' }}>
            kg
          </Button>
        </Space.Compact>

        <Space.Compact>
          <Button type="default" style={{ pointerEvents: 'none' }}>
            Maximal weight
          </Button>
          <Input
            type="number"
            step="0.001"
            value={maximalWeight}
            onChange={(event: ChangeEvent<HTMLInputElement>): void => {
              setMaximalWeight(Number(event.target.value));
            }}
          />
          <Button type="default" style={{ pointerEvents: 'none' }}>
            kg
          </Button>
        </Space.Compact>
      </Space>

      <Space size={SPACING.lg} orientation="vertical">
        <Space.Compact>
          <Button type="default" style={{ pointerEvents: 'none' }}>
            Minimal price
          </Button>
          <Input
            type="number"
            step="0.001"
            value={minimalPrice}
            onChange={(event: ChangeEvent<HTMLInputElement>): void => {
              setMinimalPrice(Number(event.target.value));
            }}
          />
        </Space.Compact>

        <Space.Compact>
          <Button type="default" style={{ pointerEvents: 'none' }}>
            Maximal price
          </Button>
          <Input
            type="number"
            step="0.001"
            value={maximalPrice}
            onChange={(event: ChangeEvent<HTMLInputElement>): void => {
              setMaximalPrice(Number(event.target.value));
            }}
          />
        </Space.Compact>
      </Space>

      <Space size={SPACING.lg} orientation="vertical">
        <Space.Compact>
          <Button type="default" style={{ pointerEvents: 'none' }}>
            Minimal description length
          </Button>
          <Input
            type="number"
            step="1"
            value={minimalDescriptionLength}
            onChange={(event: ChangeEvent<HTMLInputElement>): void => {
              setMinimalDescriptionLength(Number(event.target.value));
            }}
          />
        </Space.Compact>
      </Space>

      <Space size={SPACING.lg} orientation="vertical">
        <Button
          type="primary"
          disabled={!warehouseUuid}
          onClick={(): void => {
            if (!warehouseUuid) {
              return;
            }

            dispatch(
              cb(
                warehouseUuid,
                minimalWeight,
                maximalWeight,
                minimalPrice,
                maximalPrice,
                minimalDescriptionLength,
              ),
            );
          }}
        >
          <ReloadOutlined />
        </Button>
      </Space>
    </Space>
  );
}

export default WarehouseProductFilter;
